<script setup lang="ts">
import { ref, nextTick, watch, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n(); // Ajout pour rendre $t disponible si nécessaire, bien que le template utilise $t auto-injecté, parfois l'auto-import échoue ou cause des conflits de types.

const props = defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits(["close"]);

const messages = ref<
  Array<{
    id: number;
    text: string;
    sender: "user" | "agent";
    timestamp: Date;
  }>
>([
  {
    id: 1,
    text: "Bonjour ! Comment pouvons-nous vous aider aujourd'hui ?",
    sender: "agent",
    timestamp: new Date(),
  },
]);

const newMessage = ref("");
const messagesContainer = ref<HTMLElement | null>(null);
const sessionId = ref("");
let pollInterval: any = null;

const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

watch(
  () => props.isOpen,
  (val) => {
    if (val) {
      scrollToBottom();
    }
  },
);

const checkForNewMessages = async () => {
  if (!sessionId.value) return;
  try {
    const res = await $fetch<{ messages: any[] }>("/api/chat/poll", {
      query: { sessionId: sessionId.value },
    });

    if (res && res.messages && res.messages.length > 0) {
      res.messages.forEach((msg) => {
        messages.value.push({
          id: Date.now() + Math.random(),
          text: msg.text,
          sender: "agent",
          timestamp: new Date(msg.timestamp),
        });
      });
      scrollToBottom();
    }
  } catch (error) {
    // Silent error for polling
  }
};

const sendMessage = async () => {
  if (!newMessage.value.trim()) return;

  const currentMessage = newMessage.value;

  // Add user message locally
  messages.value.push({
    id: Date.now(),
    text: currentMessage,
    sender: "user",
    timestamp: new Date(),
  });

  newMessage.value = "";
  scrollToBottom();

  const { user } = useAuth();

  try {
    // Call server to send to Slack/WhatsApp
    await $fetch("/api/chat/send", {
      method: "POST",
      body: {
        text: currentMessage,
        sessionId: sessionId.value,
        url: window.location.href,
        user: user.value,
      },
    });

    // NOTE: Removed simulated response timeout.
    // Responses now come asynchronously via polling from Slack Integration.
  } catch (error) {
    console.error("Failed to send message", error);
    messages.value.push({
      id: Date.now() + 2,
      text: "Désolé, une erreur est survenue lors de l'envoi de votre message.",
      sender: "agent",
      timestamp: new Date(),
    });
    scrollToBottom();
  }
};

// Auto-save history to localStorage & Session Management
const STORAGE_KEY = "chat_history";
const SESSION_KEY = "chat_session_id";

onMounted(() => {
  // 1. Restore Session ID or Create New
  const storedSession = localStorage.getItem(SESSION_KEY);
  if (storedSession) {
    sessionId.value = storedSession;
  } else {
    // Generate simple ID
    const newId =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    sessionId.value = newId;
    localStorage.setItem(SESSION_KEY, newId);
  }

  // 2. Restore History
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      messages.value = parsed.map((m: any) => ({
        ...m,
        timestamp: new Date(m.timestamp),
      }));
    } catch (e) {
      console.error("Failed to load chat history", e);
    }
  }

  watch(
    messages,
    (val) => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(val));
    },
    { deep: true },
  );

  // 3. Start Polling
  pollInterval = setInterval(checkForNewMessages, 3000); // Check every 3 seconds
});

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval);
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="transform opacity-0 translate-y-4 scale-95"
      enter-to-class="transform opacity-100 translate-y-0 scale-100"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="transform opacity-100 translate-y-0 scale-100"
      leave-to-class="transform opacity-0 translate-y-4 scale-95"
    >
      <div
        v-if="isOpen"
        class="fixed bottom-24 right-6 w-[350px] md:w-[380px] h-[500px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-100 dark:border-gray-700 z-[9999]"
      >
        <!-- Header -->
        <div
          class="bg-primary p-4 flex items-center justify-between text-white shadow-md relative z-10"
        >
          <div class="flex items-center gap-3">
            <div class="relative">
              <div
                class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-xl backdrop-blur-sm"
              >
                🤖
              </div>
              <span
                class="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-primary rounded-full"
              ></span>
            </div>
            <div>
              <h3 class="font-bold text-sm">xPirience Contact Center</h3>
              <p class="text-xs text-white/80">En ligne</p>
            </div>
          </div>
          <button
            @click="$emit('close')"
            class="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Fermer le chat"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-6 h-6"
            >
              <path
                fill-rule="evenodd"
                d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>

        <!-- Messages Area -->
        <div
          ref="messagesContainer"
          class="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900 scroll-smooth"
        >
          <div class="text-center text-xs text-gray-400 my-4">Aujourd'hui</div>

          <div
            v-for="msg in messages"
            :key="msg.id"
            class="flex flex-col"
            :class="msg.sender === 'user' ? 'items-end' : 'items-start'"
          >
            <div
              class="max-w-[85%] px-4 py-2.5 rounded-2xl text-sm shadow-sm relative group"
              :class="
                msg.sender === 'user'
                  ? 'bg-primary text-white rounded-tr-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-100 dark:border-gray-700 rounded-tl-sm'
              "
            >
              {{ msg.text }}
              <div
                class="text-[10px] opacity-70 mt-1 text-right"
                :class="
                  msg.sender === 'user' ? 'text-white/80' : 'text-gray-400'
                "
              >
                {{
                  msg.timestamp.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                }}
              </div>
            </div>
          </div>
        </div>

        <!-- Input Area -->
        <div
          class="p-4 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700"
        >
          <form
            @submit.prevent="sendMessage"
            class="relative flex items-center gap-2"
          >
            <input
              v-model="newMessage"
              type="text"
              placeholder="Écrivez votre message..."
              class="w-full px-4 py-3 bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
            />
            <button
              type="submit"
              :disabled="!newMessage.trim()"
              class="absolute right-2 p-2 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              aria-label="Envoyer le message"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-5 h-5"
              >
                <path
                  d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z"
                />
              </svg>
            </button>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Custom scrollbar for webkit */
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 20px;
}
</style>
